/**
 * Product Compare Feature with Metafields Support
 * Handles product comparison functionality with sticky bar and full-screen view
 * Dynamically shows only common metafields across all selected products
 */

class ProductCompare {
  constructor() {
    this.state = {
      isActive: false,
      isExpanded: false,
      isCollapsed: false,
      selectedProducts: [],
      maxProducts: 3,
      visibleFilters: ['image', 'title', 'price', 'type', 'description', 'availability'],
      commonMetafields: [] // Metafields shared by all selected products
    };

    // Metafield configuration - maps metafield namespaces to display names
    this.metafieldConfig = {
      'custom.colour_name': 'Colour Name',
      'custom.fit': 'Fit',
      'custom.fabric_composition': 'Fabric Composition',
      'custom.care_guide': 'Care Guide',
      'custom.gender': 'Gender',
      'custom.product_specification': 'Product Specification',
      'custom.size_guide': 'Size Guide',
      'custom.model_measurement': 'Model Measurement',
      'custom.badges': 'Badges'
    };

    this.elements = {
      compareBar: document.querySelector('[data-compare-bar]'),
      compareToggle: document.querySelector('[data-compare-toggle]'),
      compareProductsList: document.querySelector('[data-compare-products-list]'),
      compareClearBtn: document.querySelector('[data-compare-clear]'),
      compareExpandBtn: document.querySelector('[data-compare-expand]'),
      compareCloseBtn: document.querySelector('[data-compare-close]'),
      compareCollapseToggle: document.querySelector('[data-compare-collapse-toggle]'),
      compareGrid: document.querySelector('[data-compare-grid]'),
      compareFiltersClearBtn: document.querySelector('[data-compare-filters-clear]'),
      metafieldFiltersContainer: document.querySelector('[data-compare-metafield-filters]'),
      productList: document.querySelector('.product-list'),
      collectionToolbar: document.querySelector('.collection-toolbar')
    };

    this.templates = {
      barProduct: document.getElementById('compare-bar-product-template'),
      compareProduct: document.getElementById('compare-product-template'),
      metafield: document.getElementById('compare-metafield-template'),
      metafieldFilter: document.getElementById('compare-metafield-filter-template')
    };

    this.init();
  }

  init() {
    if (!this.elements.compareBar) {
      console.warn('Compare bar not found');
      return;
    }

    this.addCompareToggle();
    this.bindEvents();
  }

  addCompareToggle() {
    // Add compare toggle button to collection toolbar (desktop)
    if (!this.elements.compareToggle) {
      const buttonList = this.elements.collectionToolbar?.querySelector('.collection-toolbar__button-list');
      if (buttonList) {
        const toggleContainer = document.createElement('div');
        toggleContainer.className = 'collection-toolbar__button-container';
        toggleContainer.innerHTML = `
          <label class="compare-toggle" data-compare-toggle-wrapper>
            <input type="checkbox" class="compare-toggle__input" data-compare-toggle aria-label="Toggle compare mode">
            <span class="compare-toggle__label">Compare</span>
          </label>
        `;
        buttonList.insertBefore(toggleContainer, buttonList.firstChild);
        this.elements.compareToggle = toggleContainer.querySelector('[data-compare-toggle]');
      }
    }

    // Add compare toggle to filter drawer (mobile)
    const facetsDrawer = document.querySelector('#facets-drawer');
    
    if (facetsDrawer) {
      const facetsForm = facetsDrawer.querySelector('form');
      if (facetsForm) {
        const mobileToggleContainer = document.createElement('div');
        mobileToggleContainer.className = 'facets-drawer__compare-toggle';
        mobileToggleContainer.style.cssText = 'padding: 1rem; border-bottom: 1px solid rgb(var(--text-color) / 0.1);';
        mobileToggleContainer.innerHTML = `
          <label class="compare-toggle text-xxs" data-compare-toggle-mobile-wrapper>
            <input type="checkbox" class="compare-toggle__input" data-compare-toggle-mobile aria-label="Toggle compare mode">
            <span class="compare-toggle__label text-xxs">Compare</span>
          </label>
        `;
        facetsForm.insertBefore(mobileToggleContainer, facetsForm.firstChild);
        
        const mobileToggle = mobileToggleContainer.querySelector('[data-compare-toggle-mobile]');
        if (mobileToggle && this.elements.compareToggle) {
          mobileToggle.addEventListener('change', () => {
            this.elements.compareToggle.checked = mobileToggle.checked;
            this.toggleCompareMode();
          });
        }
      }
    }
  }

  bindEvents() {
    if (this.elements.compareToggle) {
      this.elements.compareToggle.addEventListener('change', () => this.toggleCompareMode());
    }

    if (this.elements.compareClearBtn) {
      this.elements.compareClearBtn.addEventListener('click', () => this.clearAll());
    }

    if (this.elements.compareExpandBtn) {
      this.elements.compareExpandBtn.addEventListener('click', () => this.expandComparison());
    }

    if (this.elements.compareCloseBtn) {
      this.elements.compareCloseBtn.addEventListener('click', () => this.closeComparison());
    }

    if (this.elements.compareCollapseToggle) {
      this.elements.compareCollapseToggle.addEventListener('click', () => this.toggleBarCollapse());
    }

    if (this.elements.compareFiltersClearBtn) {
      this.elements.compareFiltersClearBtn.addEventListener('click', () => this.clearFilters());
    }

    // Bind event listeners to static filter checkboxes
    this.bindFilterEvents();

    if (this.elements.productList) {
      this.elements.productList.addEventListener('change', (e) => {
        const checkbox = e.target.closest('[data-compare-checkbox-input]');
        if (checkbox) {
          this.handleCheckboxChange(checkbox);
        }
      });
    }

    window.addEventListener('beforeunload', () => {
      if (this.state.isActive) {
        this.toggleCompareMode();
      }
    });

    this.addDragScroll();
  }

  bindFilterEvents() {
    // Bind to all existing filter checkboxes
    const filterCheckboxes = document.querySelectorAll('[data-compare-filter]');
    filterCheckboxes.forEach(filter => {
      filter.addEventListener('change', () => this.updateFilters());
    });
  }

  toggleCompareMode() {
    this.state.isActive = !this.state.isActive;

    if (this.elements.compareToggle) {
      this.elements.compareToggle.checked = this.state.isActive;
    }

    const mobileToggle = document.querySelector('[data-compare-toggle-mobile]');
    if (mobileToggle) {
      mobileToggle.checked = this.state.isActive;
    }

    if (this.elements.productList) {
      this.elements.productList.classList.toggle('compare-mode-active', this.state.isActive);
    }

    if (!this.state.isActive) {
      this.clearAll();
      this.hideCompareBar();
    }
  }

  handleCheckboxChange(checkbox) {
    const productId = checkbox.dataset.productId;
    const isChecked = checkbox.checked;

    if (isChecked) {
      if (this.state.selectedProducts.length >= this.state.maxProducts) {
        checkbox.checked = false;
        return;
      }
      this.addProduct(productId, checkbox);
    } else {
      this.removeProduct(productId);
    }

    this.updateCheckboxStates();
    this.updateCompareBar();
  }

  async addProduct(productId, checkbox) {
    const productHandle = checkbox.dataset.productHandle;
    
    const productData = await this.fetchProductData(productHandle);
    
    if (productData) {
      this.state.selectedProducts.push({
        id: productId,
        handle: productHandle,
        data: productData
      });

      const productCard = checkbox.closest('.product-card');
      if (productCard) {
        productCard.classList.add('is-compare-selected');
      }

      // Recalculate common metafields
      this.updateCommonMetafields();
      this.updateCompareBar();
    }
  }

  removeProduct(productId) {
    const index = this.state.selectedProducts.findIndex(p => p.id === productId);
    if (index > -1) {
      this.state.selectedProducts.splice(index, 1);

      const checkbox = document.querySelector(`[data-product-id="${productId}"][data-compare-checkbox-input]`);
      if (checkbox) {
        checkbox.checked = false;
        const productCard = checkbox.closest('.product-card');
        if (productCard) {
          productCard.classList.remove('is-compare-selected');
        }
      }

      // Recalculate common metafields
      this.updateCommonMetafields();
      this.updateCompareBar();
    }
  }

  updateCommonMetafields() {
    if (this.state.selectedProducts.length === 0) {
      this.state.commonMetafields = [];
      return;
    }

    // Get all metafield keys from all products
    const allMetafieldKeys = this.state.selectedProducts.map(product => {
      const metafields = product.data.metafields || {};
      return Object.keys(metafields).filter(key => this.metafieldConfig[key]);
    });

    // Find metafields that exist in ALL products
    if (allMetafieldKeys.length > 0) {
      this.state.commonMetafields = allMetafieldKeys[0].filter(key => 
        allMetafieldKeys.every(keys => keys.includes(key))
      );
    } else {
      this.state.commonMetafields = [];
    }
  }

  updateCheckboxStates() {
    const checkboxes = document.querySelectorAll('[data-compare-checkbox-input]');
    const isMaxReached = this.state.selectedProducts.length >= this.state.maxProducts;

    checkboxes.forEach(checkbox => {
      if (!checkbox.checked && isMaxReached) {
        checkbox.disabled = true;
      } else {
        checkbox.disabled = false;
      }
    });
  }

  updateCompareBar() {
    const countElement = document.querySelector('[data-compare-selected-count]');
    if (countElement) {
      countElement.textContent = `${this.state.selectedProducts.length}`;
    }

    if (this.elements.compareProductsList) {
      this.elements.compareProductsList.innerHTML = '';
      
      this.state.selectedProducts.forEach(product => {
        const productEl = this.createBarProductElement(product);
        this.elements.compareProductsList.appendChild(productEl);
      });

      const placeholdersNeeded = this.state.maxProducts - this.state.selectedProducts.length;
      for (let i = 0; i < placeholdersNeeded; i++) {
        const placeholderEl = this.createPlaceholderElement(this.state.selectedProducts.length + i + 1);
        this.elements.compareProductsList.appendChild(placeholderEl);
      }
    }

    if (this.elements.compareExpandBtn) {
      this.elements.compareExpandBtn.disabled = this.state.selectedProducts.length < 2;
      this.elements.compareExpandBtn.textContent = `Compare`;
    }

    if (this.state.selectedProducts.length > 0) {
      this.showCompareBar();
    } else {
      this.hideCompareBar();
    }
  }

  createBarProductElement(product) {
    const template = this.templates.barProduct.content.cloneNode(true);
    const container = template.querySelector('.compare-bar__product-item');
    const image = template.querySelector('[data-product-image]');
    const title = template.querySelector('[data-product-title]');
    const price = template.querySelector('[data-product-price]');
    const removeBtn = template.querySelector('[data-compare-remove]');

    container.dataset.productId = product.id;
    
    if (image && product.data.featured_image) {
      image.src = this.getImageUrl(product.data.featured_image, '100x100');
      image.alt = product.data.title;
    }

    if (title) {
      title.textContent = product.data.title;
    }

    if (price) {
      price.textContent = this.formatPrice(product.data.price);
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.removeProduct(product.id));
    }

    return template;
  }

  createPlaceholderElement(slotNumber) {
    const placeholder = document.createElement('div');
    placeholder.className = 'compare-bar__product-placeholder';
    placeholder.innerHTML = `
      <div class="compare-bar__placeholder-content">
        <svg class="compare-bar__placeholder-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <p class="compare-bar__placeholder-text">Select product ${slotNumber}</p>
      </div>
    `;
    return placeholder;
  }

  showCompareBar() {
    if (this.elements.compareBar) {
      this.elements.compareBar.classList.add('is-visible');
    }
  }

  hideCompareBar() {
    if (this.elements.compareBar) {
      this.elements.compareBar.classList.remove('is-visible', 'is-expanded');
      this.state.isExpanded = false;
      this.state.isCollapsed = false;
    }
  }

  toggleBarCollapse() {
    this.state.isCollapsed = !this.state.isCollapsed;
    if (this.elements.compareBar) {
      this.elements.compareBar.classList.toggle('is-collapsed', this.state.isCollapsed);
    }
  }

  clearAll() {
    this.state.selectedProducts.forEach(product => {
      const checkbox = document.querySelector(`[data-product-id="${product.id}"][data-compare-checkbox-input]`);
      if (checkbox) {
        checkbox.checked = false;
        const productCard = checkbox.closest('.product-card');
        if (productCard) {
          productCard.classList.remove('is-compare-selected');
        }
      }
    });

    this.state.selectedProducts = [];
    this.state.commonMetafields = [];
    this.updateCheckboxStates();
    this.updateCompareBar();
  }

  expandComparison() {
    if (this.state.selectedProducts.length < 2) return;

    this.state.isExpanded = true;
    if (this.elements.compareBar) {
      this.elements.compareBar.classList.add('is-expanded');
      document.body.style.overflow = 'hidden';
    }

    this.renderMetafieldFilters();
    this.renderComparisonGrid();
  }

  closeComparison() {
    this.state.isExpanded = false;
    if (this.elements.compareBar) {
      this.elements.compareBar.classList.remove('is-expanded');
      document.body.style.overflow = '';
    }
  }

  renderMetafieldFilters() {
    if (!this.elements.metafieldFiltersContainer) return;

    // Clear existing metafield filters
    this.elements.metafieldFiltersContainer.innerHTML = '';

    // Add filter checkbox for each common metafield
    this.state.commonMetafields.forEach(metafieldKey => {
      const displayName = this.metafieldConfig[metafieldKey];
      const filterEl = this.templates.metafieldFilter.content.cloneNode(true);
      const checkbox = filterEl.querySelector('input');
      const label = filterEl.querySelector('label');
      const filterId = `filter-${metafieldKey.replace(/\./g, '-')}`;

      checkbox.id = filterId;
      checkbox.value = metafieldKey;
      label.setAttribute('for', filterId);
      label.textContent = displayName;

      // Add to visible filters by default
      if (!this.state.visibleFilters.includes(metafieldKey)) {
        this.state.visibleFilters.push(metafieldKey);
      }

      // Bind event
      checkbox.addEventListener('change', () => this.updateFilters());

      this.elements.metafieldFiltersContainer.appendChild(filterEl);
    });
  }

  renderComparisonGrid() {
    if (!this.elements.compareGrid) return;

    this.elements.compareGrid.innerHTML = '';

    const columnClass = this.state.selectedProducts.length === 2 ? 'compare-grid--2-cols' : 'compare-grid--3-cols';
    this.elements.compareGrid.className = `compare-grid ${columnClass}`;

    this.state.selectedProducts.forEach(product => {
      const productEl = this.createCompareProductElement(product);
      this.elements.compareGrid.appendChild(productEl);
    });

    this.updateFilters();
  }

  createCompareProductElement(product) {
    const template = this.templates.compareProduct.content.cloneNode(true);
    const data = product.data;

    // Image
    const image = template.querySelector('[data-product-image]');
    if (image && data.featured_image) {
      image.src = this.getImageUrl(data.featured_image, '600x600');
      image.alt = data.title;
    }

    // Title
    const title = template.querySelector('[data-product-title]');
    if (title) {
      title.textContent = data.title;
    }

    // Price
    const priceContainer = template.querySelector('[data-product-price]');
    if (priceContainer) {
      let priceHTML = `<span class="compare-product__price-current">${this.formatPrice(data.price)}</span>`;
      
      if (data.compare_at_price && data.compare_at_price > data.price) {
        priceHTML = `<span class="compare-product__price-compare">${this.formatPrice(data.compare_at_price)}</span>` + priceHTML;
      }
      
      priceContainer.innerHTML = priceHTML;
    }

    // Product Type
    const type = template.querySelector('[data-product-type]');
    if (type) {
      type.textContent = data.type || 'N/A';
    }

    // Description
    const description = template.querySelector('[data-product-description]');
    if (description) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = data.description || '';
      description.textContent = tempDiv.textContent || 'No description available';
    }

    // Availability
    const availability = template.querySelector('[data-product-availability]');
    if (availability) {
      const isAvailable = data.available;
      const badgeClass = isAvailable ? 'compare-attribute__badge' : 'compare-attribute__badge compare-attribute__badge--out-of-stock';
      availability.innerHTML = `<span class="${badgeClass}">${isAvailable ? 'In Stock' : 'Out of Stock'}</span>`;
    }

    // Add common metafields
    const metafieldsContainer = template.querySelector('[data-product-metafields]');
    if (metafieldsContainer) {
      this.state.commonMetafields.forEach(metafieldKey => {
        const metafieldValue = data.metafields?.[metafieldKey];
        if (metafieldValue) {
          const metafieldEl = this.createMetafieldElement(metafieldKey, metafieldValue);
          metafieldsContainer.appendChild(metafieldEl);
        }
      });
    }

    return template;
  }

  createMetafieldElement(metafieldKey, value) {
    const template = this.templates.metafield.content.cloneNode(true);
    const container = template.querySelector('.compare-attribute');
    const label = template.querySelector('.compare-attribute__label');
    const valueEl = template.querySelector('.compare-attribute__value');

    container.dataset.attribute = metafieldKey;
    label.textContent = this.metafieldConfig[metafieldKey];
    
    // Handle different metafield value types
    if (typeof value === 'object' && value !== null) {
      // Rich text or structured data
      valueEl.innerHTML = value.value || JSON.stringify(value);
    } else {
      valueEl.textContent = value || 'N/A';
    }

    return template;
  }

  updateFilters() {
    // Get ALL filter checkboxes (including dynamically added ones)
    const allFilterCheckboxes = document.querySelectorAll('[data-compare-filter]');
    
    // Get all checked filters
    this.state.visibleFilters = Array.from(allFilterCheckboxes)
      .filter(filter => filter.checked)
      .map(filter => filter.value);

    // Update visibility of all attributes
    const allAttributes = document.querySelectorAll('[data-attribute]');
    allAttributes.forEach(attr => {
      const attrType = attr.dataset.attribute;
      const isVisible = this.state.visibleFilters.includes(attrType);
      attr.style.display = isVisible ? '' : 'none';
    });
  }

  clearFilters() {
    // Reset all filter checkboxes to checked
    const allFilters = document.querySelectorAll('[data-compare-filter]');
    allFilters.forEach(filter => {
      filter.checked = true;
    });
    this.updateFilters();
  }

  async fetchProductData(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      const data = await response.json();
      
      // Transform metafields from Shopify format to our format
      if (data.metafields) {
        const transformedMetafields = {};
        
        // Shopify's product.js returns metafields in various formats
        // We need to normalize them to namespace.key format
        Object.keys(data.metafields).forEach(key => {
          const metafield = data.metafields[key];
          
          // Handle both flat and nested metafield structures
          if (typeof metafield === 'object' && metafield.namespace && metafield.key) {
            const fullKey = `${metafield.namespace}.${metafield.key}`;
            transformedMetafields[fullKey] = metafield.value;
          } else {
            // If already in correct format or simple value
            transformedMetafields[key] = metafield;
          }
        });
        
        data.metafields = transformedMetafields;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  formatPrice(price) {
    // Price comes in cents from Shopify API
    const amount = price / 100;
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  getImageUrl(imageUrl, size) {
    // Shopify image URL transformation
    if (!imageUrl) return '';
    
    // Remove any existing size suffix
    const cleanUrl = imageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\./g, '.');
    
    // Add new size
    return cleanUrl.replace(/\.([^.]*)$/, `_${size}.$1`);
  }

  addDragScroll() {
    const productsList = this.elements.compareProductsList;
    if (!productsList) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    productsList.addEventListener('mousedown', (e) => {
      // Don't interfere with button clicks
      if (e.target.closest('button')) return;
      
      isDown = true;
      productsList.style.cursor = 'grabbing';
      startX = e.pageX - productsList.offsetLeft;
      scrollLeft = productsList.scrollLeft;
    });

    productsList.addEventListener('mouseleave', () => {
      isDown = false;
      productsList.style.cursor = 'grab';
    });

    productsList.addEventListener('mouseup', () => {
      isDown = false;
      productsList.style.cursor = 'grab';
    });

    productsList.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - productsList.offsetLeft;
      const walk = (x - startX) * 2;
      productsList.scrollLeft = scrollLeft - walk;
    });

    // Touch events for mobile swipe
    let touchStartX = 0;
    let touchScrollLeft = 0;

    productsList.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX;
      touchScrollLeft = productsList.scrollLeft;
    }, { passive: true });

    productsList.addEventListener('touchmove', (e) => {
      const touchX = e.touches[0].pageX;
      const walk = (touchStartX - touchX);
      productsList.scrollLeft = touchScrollLeft + walk;
    }, { passive: true });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCompare = new ProductCompare();
  });
} else {
  window.productCompare = new ProductCompare();
}